import { useState, useCallback, useEffect, useRef } from 'react';
import { Company, CompanyHelpers } from '../types/company';
import { CompanyService } from '../services/company'; // Pour les mocks
import companyApiService from '../services/companyApi'; // Pour l'API réelle
import cloudinaryService from '../services/cloudinaryService'; // Pour les uploads Cloudinary
import { useToast } from './useToast';
import { useUser } from './useUser';
import { CompanyPersistence } from '../utils/companyPersistence';
import { checkBackendHealth, isBackendReady } from '../utils/backendHealth';
import { cacheService } from '../services/cacheService';

// Création des instances de service
const mockCompanyService = new CompanyService();

/**
 * Valider les données avant envoi et retourner les champs manquants
 */
function validateCompanyData(formData: Partial<Company>): { valid: boolean; missingFields: string[] } {
  const missingFields: string[] = [];
  
  // Vérifier les champs obligatoires pour le backend
  if (!formData.raisonSociale || formData.raisonSociale.trim() === '') {
    missingFields.push('Raison sociale de l\'entreprise');
  }
  
  if (!formData.typeEntreprise) {
    missingFields.push('Type d\'entreprise (Traditional/Startup)');
  }
  
  if (!formData.secteurActivitePrincipal || formData.secteurActivitePrincipal.trim() === '') {
    missingFields.push('Secteur d\'activité principal');
  }
  
  // Recommandations (non bloquantes mais importantes)
  const recommendations: string[] = [];
  
  if (!formData.email || formData.email.trim() === '') {
    recommendations.push('Email de contact de l\'entreprise (recommandé)');
  }
  
  if (!formData.telephoneMobile && !formData.telephoneFixe) {
    recommendations.push('Au moins un numéro de téléphone (recommandé)');
  }
  
  // Si des recommandations mais pas de champs manquants, on laisse passer avec un avertissement
  if (recommendations.length > 0 && missingFields.length === 0) {
    console.warn('⚠️ Recommandations:', recommendations);
  }
  
  return {
    valid: missingFields.length === 0,
    missingFields
  };
}

/**
 * ============================================
 * ✨ COMPATIBILITÉ DIRECTE - PAS D'ADAPTATEURS
 * ============================================
 * 
 * L'interface Company dans src/types/company.ts est PLATE et correspond
 * exactement à ce que le backend attend et retourne.
 * 
 * STRUCTURE PLATE (UI = API):
 * - raisonSociale, sigle, formeJuridiqueOHADA
 * - email, telephoneMobile, telephoneFixe (PAS contacts{})
 * - siegeSocial: Coordinates (PAS address{})
 * - secteurActivitePrincipal, secteursActiviteSecondaires (PAS activities{})
 * - capitalSocial, deviseCapital (PAS capital{})
 * - dirigeants[], actionnaires[] (PAS owner{}, associates{})
 * 
 * Le backend doit accepter et retourner EXACTEMENT cette structure.
 */

/**
 * Préparer les données pour l'envoi au backend
 * ✨ DIRECT: Pas de transformation complexe, juste des ajustements mineurs
 */
function prepareDataForBackend(formData: Partial<Company>): Partial<Company> {
  // Copie des données sans modification de structure
  const preparedData: Partial<Company> = { ...formData };
  
  // S'assurer que les champs obligatoires backend sont présents
  if (formData.raisonSociale && !preparedData.name) {
    preparedData.name = formData.raisonSociale;
  }
  
  // Type par défaut
  if (!preparedData.type) {
    preparedData.type = 'sme';
  }
  
  // Status par défaut
  if (!preparedData.status) {
    preparedData.status = 'active';
  }
  
  // Ne pas envoyer l'ID lors de la création
  if (!formData.id) {
    delete preparedData.id;
  }
  
  console.log('📤 Données préparées pour le backend (structure plate):', preparedData);
  return preparedData;
}

/**
 * Parser les erreurs du backend pour extraire des messages clairs
 */
function parseBackendError(error: any): string {
  // Erreur avec message détaillé du backend (validation)
  if (error.response?.data?.message) {
    const backendMessage = error.response.data.message;
    
    // Si c'est un tableau de messages (validation)
    if (Array.isArray(backendMessage)) {
      return 'Données invalides :\n• ' + backendMessage.join('\n• ');
    }
    
    // Si c'est un string
    if (typeof backendMessage === 'string') {
      return backendMessage;
    }
  }
  
  // Erreur réseau
  if (error.message === 'Network Error' || error.code === 'ERR_NETWORK') {
    return 'Impossible de contacter le serveur. Vérifiez votre connexion internet.';
  }
  
  // Erreur d'authentification
  if (error.response?.status === 401) {
    return 'Session expirée. Veuillez vous reconnecter.';
  }
  
  // Erreur de permission
  if (error.response?.status === 403) {
    return 'Vous n\'avez pas les permissions nécessaires pour cette action.';
  }
  
  // Erreur serveur
  if (error.response?.status >= 500) {
    return 'Erreur du serveur. Veuillez réessayer plus tard.';
  }
  
  // Message générique
  return error.message || 'Une erreur inattendue s\'est produite.';
}

interface UseCompanyOptions {
  enabled?: boolean;
  preferMock?: boolean; // Option pour préférer les données mockées (tests/développement)
}

export function useCompany(companyId: string | undefined, options: UseCompanyOptions = { enabled: true, preferMock: false }) {
  const [company, setCompany] = useState<Company | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [isUpdating, setIsUpdating] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);
  const [usingMockData, setUsingMockData] = useState<boolean>(options.preferMock || false);
  const [dataSource, setDataSource] = useState<'cache' | 'network' | 'none'>('none');
  const [isRevalidating, setIsRevalidating] = useState<boolean>(false);
  
  // Utiliser les hooks
  const toast = useToast();
  const { user, updateProfile } = useUser();
  
  // ✨ SIMPLIFICATION: Ref pour tracker l'ID en cours de chargement (évite les appels multiples)
  const loadingIdRef = useRef<string | null>(null);
  const lastLoadedIdRef = useRef<string | null>(null);
  const lastBackendFetchRef = useRef<number>(0);
  
  // Mémoriser les valeurs stables pour éviter les re-renders
  const userId = user?.id;

  // ============================================
  // 📦 CHARGEMENT DEPUIS INDEXEDDB (CACHE-FIRST)
  // ============================================
  
  const loadFromIndexedDB = useCallback(async (cId: string): Promise<Company | null> => {
    try {
      const cached = await cacheService.get<Company>('company', cId);
      if (cached) {
        console.log('📦 [Company] Chargé depuis IndexedDB:', cached.data?.raisonSociale);
        return cached.data;
      }
    } catch (err) {
      console.warn('⚠️ [Company] Erreur lecture IndexedDB:', err);
    }
    return null;
  }, []);

  // ============================================
  // 🔄 REVALIDATION BACKGROUND (SYNC BACKEND)
  // ============================================
  
  const revalidateFromBackend = useCallback(async (cId: string): Promise<Company | null> => {
    // Anti-rebond: minimum 5 secondes entre les appels backend
    const now = Date.now();
    if (now - lastBackendFetchRef.current < 5000) {
      console.log('⏳ [Company] Revalidation ignorée (trop rapprochée)');
      return null;
    }
    lastBackendFetchRef.current = now;

    try {
      console.log('🔄 [Company] Revalidation depuis le backend...');
      setIsRevalidating(true);
      
      const backendData = await companyApiService.getCompany(cId);
      
      if (backendData) {
        // Vérifier si les données ont changé
        const cached = await cacheService.get<Company>('company', cId);
        const hasChanges = !cached || cacheService.hasDataChanged(cached, backendData);
        
        if (hasChanges) {
          console.log('✨ [Company] Nouvelles données détectées, mise à jour du cache...');
          // Mettre à jour IndexedDB
          if (userId) {
            await cacheService.set('company', cId, backendData, userId, 'synced');
          }
          return backendData as Company;
        } else {
          console.log('✅ [Company] Données identiques, pas de mise à jour nécessaire');
          // Juste mettre à jour le timestamp
          await cacheService.updateSyncStatus('company', cId, 'synced');
        }
      }
      return null;
    } catch (err) {
      console.error('❌ [Company] Erreur revalidation backend:', err);
      throw err;
    } finally {
      setIsRevalidating(false);
    }
  }, [userId]);

  // ✨ OPTIMISATION: Fonction de chargement avec stratégie Cache-First
  const loadCompany = useCallback(async (cId: string) => {
    // Éviter les appels redondants
    if (loadingIdRef.current === cId || lastLoadedIdRef.current === cId) {
      console.log('⏸️ Chargement déjà en cours ou terminé pour:', cId);
      return;
    }
    
    loadingIdRef.current = cId;
    setLoading(true);
    setError(null);
    
    try {
      // ============================================
      // 1️⃣ ÉTAPE 1: Charger depuis IndexedDB (instantané)
      // ============================================
      const cachedCompany = await loadFromIndexedDB(cId);
      
      if (cachedCompany) {
        console.log('✅ [Company] Données affichées depuis le cache');
        setCompany(cachedCompany);
        setDataSource('cache');
        lastLoadedIdRef.current = cId;
        setLoading(false); // Arrêter le loading - les données sont affichées
        
        // ============================================
        // 2️⃣ ÉTAPE 2: Revalider en background (silencieux)
        // ============================================
        // Vérifier si le cache est encore frais (moins de 5 minutes)
        const cached = await cacheService.get<Company>('company', cId);
        if (cached && !cacheService.isCacheValid(cached.metadata)) {
          console.log('🔄 [Company] Cache expiré, revalidation en background...');
          revalidateFromBackend(cId).then(updatedData => {
            if (updatedData) {
              setCompany(updatedData);
              setDataSource('network');
              setUsingMockData(false);
            }
          }).catch(err => {
            console.warn('⚠️ [Company] Revalidation échouée, données du cache utilisées');
          });
        } else {
          console.log('✅ [Company] Cache encore valide, pas de revalidation nécessaire');
        }
        return;
      }
      
      // ============================================
      // 3️⃣ PAS DE CACHE: Charger directement depuis le backend
      // ============================================
      console.log('🌐 [Company] Pas de cache, chargement depuis le backend...');
      const backendData = await companyApiService.getCompany(cId);
      console.log('✅ [Company] Données reçues du backend:', backendData?.raisonSociale);
      
      setCompany(backendData as Company);
      setDataSource('network');
      lastLoadedIdRef.current = cId;
      setUsingMockData(false);
      
      // Sauvegarder en IndexedDB pour la prochaine fois
      if (userId && backendData) {
        await cacheService.set('company', cId, backendData, userId, 'synced');
        console.log('💾 [Company] Données sauvegardées dans IndexedDB');
      }
      
      // Aussi sauvegarder en cache localStorage (backup)
      if (userId) {
        CompanyPersistence.save(backendData as Company, userId);
      }
    } catch (err) {
      console.error('❌ Erreur chargement entreprise:', err);
      
      // En cas d'erreur, essayer le cache localStorage
      if (userId) {
        const cachedCompany = CompanyPersistence.load(userId);
        if (cachedCompany && cachedCompany.id === cId) {
          console.log('📦 Utilisation du cache localStorage après erreur');
          setCompany(cachedCompany);
          setDataSource('cache');
          lastLoadedIdRef.current = cId;
          return;
        }
      }
      
      setError(err as Error);
      toast.showToast?.({
        type: 'error',
        title: 'Échec du chargement',
        message: parseBackendError(err)
      });
    } finally {
      setLoading(false);
      loadingIdRef.current = null;
    }
  }, [userId, toast, loadFromIndexedDB, revalidateFromBackend]);

  // ✨ SIMPLIFICATION: Effet unique qui se déclenche quand companyId change
  useEffect(() => {
    if (!companyId || !options.enabled) {
      setCompany(null);
      setLoading(false);
      return;
    }
    
    // ⚠️ VALIDATION: Ne pas faire de requête avec des IDs invalides (temp-id, mock-*, etc.)
    const isValidUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(companyId);
    const isInvalidId = companyId.startsWith('temp-') || companyId.startsWith('mock-') || companyId === 'undefined' || companyId === 'null';
    
    if (isInvalidId || (!isValidUUID && !companyId.startsWith('comp-'))) {
      console.log('⏭️ [Company] ID invalide ignoré:', companyId);
      setLoading(false);
      return;
    }
    
    // Ne pas recharger si déjà chargé
    if (lastLoadedIdRef.current === companyId && company) {
      return;
    }
    
    loadCompany(companyId);
  }, [companyId, options.enabled, loadCompany]);

  // ✨ Reset quand l'utilisateur change
  useEffect(() => {
    if (!userId) {
      lastLoadedIdRef.current = null;
      setCompany(null);
    }
  }, [userId]);

  // Fonction manuelle pour forcer le refresh
  const refreshCompany = useCallback(() => {
    if (companyId) {
      lastLoadedIdRef.current = null;
      loadCompany(companyId);
    }
  }, [companyId, loadCompany]);

  const createCompany = async (companyData: Partial<Company>) => {
    setIsUpdating(true);
    setError(null);
    
    try {
      // Validation avant envoi
      const validation = validateCompanyData(companyData);
      if (!validation.valid) {
        toast.showToast?.({
          type: 'warning',
          title: 'Informations requises',
          message: `Champs manquants: ${validation.missingFields.join(', ')}`
        });
        return null;
      }
      
      let newCompany: Company;
      
      console.log('📤 Envoi de la nouvelle entreprise au backend...');
      // ✨ DIRECT: Préparer et envoyer les données (structure plate)
      const preparedData = prepareDataForBackend(companyData);
      
      // Vérifier que les champs obligatoires sont présents pour CreateCompanyRequest
      if (!preparedData.raisonSociale || !preparedData.typeEntreprise || !preparedData.secteurActivitePrincipal) {
        throw new Error('Champs obligatoires manquants pour la création');
      }
      
      const createdCompanyResponse = await companyApiService.createCompany({
        ...preparedData,
        raisonSociale: preparedData.raisonSociale,
        typeEntreprise: preparedData.typeEntreprise,
        secteurActivitePrincipal: preparedData.secteurActivitePrincipal
      });
      
      // ✨ DIRECT: Le backend retourne la structure Company
      newCompany = createdCompanyResponse as Company;
      console.log('✅ Entreprise créée au backend avec UUID:', newCompany.id);
      setUsingMockData(false);
      setDataSource('network');
      
      setCompany(newCompany);
      
      // Sauvegarder en IndexedDB et cache localStorage
      if (user?.id) {
        // IndexedDB (primaire)
        await cacheService.set('company', newCompany.id!, newCompany, user.id, 'synced');
        console.log('💾 [Company] Nouvelle entreprise sauvegardée dans IndexedDB');
        
        // localStorage (backup)
        CompanyPersistence.save(newCompany, user.id);
      }
      
      // Mettre à jour le profil utilisateur avec l'ID de la nouvelle entreprise
      try {
        console.log('🔗 Mise à jour du profil utilisateur avec companyId:', newCompany.id);
        const updatedUser = await updateProfile({ companyId: newCompany.id });
        
        if (updatedUser && updatedUser.companyId === newCompany.id) {
          console.log('✅ Profil utilisateur mis à jour avec l\'ID de l\'entreprise:', newCompany.id);
        } else {
          console.error('⚠️ La mise à jour du profil a réussi mais companyId n\'est pas défini');
          throw new Error('Le companyId n\'a pas été correctement enregistré dans le profil utilisateur');
        }
      } catch (updateError) {
        console.error('❌ Échec de la mise à jour du profil utilisateur:', updateError);
        
        toast.showToast?.({
          type: 'error',
          title: 'Avertissement',
          message: 'Entreprise créée mais impossible de l\'associer à votre profil. Veuillez rafraîchir la page ou contacter le support.'
        });
        
        return null;
      }
      
      // Afficher une notification de succès
      toast.showToast?.({
        type: 'success',
        title: 'Succès',
        message: 'Entreprise créée avec succès!'
      });
      
      return newCompany;
    } catch (err) {
      const errorMessage = parseBackendError(err);
      setError(err as Error);
      console.error('❌ Erreur lors de la création de l\'entreprise:', err);
      
      toast.showToast?.({
        type: 'error',
        title: 'Échec de la création',
        message: errorMessage
      });
      
      return null;
    } finally {
      setIsUpdating(false);
    }
  };

  const updateCompany = async (companyData: Partial<Company>) => {
    if (!companyId) {
      // Si pas d'ID, créer une nouvelle entreprise
      return await createCompany(companyData);
    }
    
    setIsUpdating(true);
    setError(null);
    
    try {
      console.log(`🔄 Mise à jour de l'entreprise ${companyId}:`, companyData);
      
      let updatedCompany: Company;
      
      console.log('📤 Envoi des données au backend...');
      // ✨ DIRECT: Préparer et envoyer les données (structure plate)
      const preparedData = prepareDataForBackend(companyData);
      
      const updatedCompanyResponse = await companyApiService.updateCompany(companyId, preparedData);
      
      // ✨ ROBUSTESSE: Vérifier que la réponse contient les données attendues
      if (!updatedCompanyResponse || !updatedCompanyResponse.id) {
        console.error('❌ Réponse invalide lors de la mise à jour:', updatedCompanyResponse);
        throw new Error('Le serveur n\'a pas retourné les données de l\'entreprise mise à jour');
      }
      
      // ✨ DIRECT: Le backend retourne la structure Company
      updatedCompany = updatedCompanyResponse as Company;
      console.log('✅ Données mises à jour au backend avec succès, id:', updatedCompany.id);
      setUsingMockData(false);
      setDataSource('network');
      
      setCompany(updatedCompany);
      
      // Mettre à jour IndexedDB et cache localStorage
      if (user?.id && updatedCompany?.id) {
        // IndexedDB (primaire)
        await cacheService.set('company', updatedCompany.id, updatedCompany, user.id, 'synced');
        console.log('💾 [Company] Données mises à jour dans IndexedDB');
        
        // localStorage (backup)
        CompanyPersistence.save(updatedCompany, user.id);
      }
      
      // Afficher une notification de succès
      toast.showToast?.({ 
        type: 'success', 
        title: 'Mise à jour réussie', 
        message: 'Les informations de l\'entreprise ont été mises à jour.'
      });
      
      return updatedCompany;
    } catch (err) {
      const errorMessage = parseBackendError(err);
      setError(err as Error);
      console.error('❌ Erreur lors de la mise à jour de l\'entreprise:', err);
      
      // Afficher une notification d'erreur détaillée
      toast.showToast?.({ 
        type: 'error', 
        title: 'Échec de la mise à jour', 
        message: errorMessage
      });
      
      return null;
    } finally {
      setIsUpdating(false);
    }
  };

  const uploadLogo = async (file: File) => {
    if (!companyId) return null;
    setIsUpdating(true);
    setError(null);
    
    try {
      console.log(`🖼️ Upload du logo pour l'entreprise ${companyId}`);
      
      // 1. Upload vers Cloudinary d'abord
      const cloudinaryResult = await cloudinaryService.uploadLogo(file, 'company');
      console.log('✅ Logo uploadé sur Cloudinary:', cloudinaryResult.url);
      
      // 2. Envoyer l'URL au backend pour mise à jour
      // Le backend ne reçoit que l'URL, pas le fichier
      await companyApiService.updateCompany(companyId, {
        logo: cloudinaryResult.url
      });
      console.log('✅ URL du logo enregistrée dans le backend');
      setUsingMockData(false);
      
      // Mettre à jour le state local
      setCompany(prev => prev ? { ...prev, logo: cloudinaryResult.url } : null);
      
      // Mettre à jour IndexedDB et cache localStorage
      if (user?.id && company) {
        const updatedCompany = { ...company, logo: cloudinaryResult.url };
        // IndexedDB (primaire)
        await cacheService.set('company', companyId, updatedCompany, user.id, 'synced');
        // localStorage (backup)
        CompanyPersistence.save(updatedCompany, user.id);
      }
      
      // Afficher une notification de succès
      toast.showToast?.({ 
        type: 'success', 
        title: 'Upload réussi', 
        message: 'Le logo de l\'entreprise a été mis à jour.'
      });
      
      return { url: cloudinaryResult.url };
    } catch (err) {
      const errorMessage = parseBackendError(err);
      setError(err as Error);
      console.error('❌ Erreur lors de l\'upload du logo:', err);
      
      // Afficher une notification d'erreur détaillée
      toast.showToast?.({ 
        type: 'error', 
        title: 'Échec de l\'upload du logo', 
        message: errorMessage
      });
      
      throw err;
    } finally {
      setIsUpdating(false);
    }
  };

  const uploadOwnerCV = async (file: File) => {
    if (!companyId) return null;
    setIsUpdating(true);
    setError(null);
    
    try {
      console.log(`📄 Upload du CV pour le propriétaire de l'entreprise ${companyId}`);
      
      // 1. Upload vers Cloudinary d'abord
      const cloudinaryResult = await cloudinaryService.uploadCV(file);
      console.log('✅ CV uploadé sur Cloudinary:', cloudinaryResult.url);
      
      // 2. Mettre à jour le state avec le nouvel URL du CV
      // Note: Le backend sera mis à jour lors de la sauvegarde globale du formulaire
      setCompany(prev => {
        if (!prev) return null;
        return CompanyHelpers.updateOwnerCV(prev, cloudinaryResult.url);
      });
      
      setUsingMockData(false);
      
      // Afficher une notification de succès
      toast.showToast?.({ 
        type: 'success', 
        title: 'Upload réussi', 
        message: 'Le CV a été mis à jour.'
      });
      
      return { url: cloudinaryResult.url };
    } catch (err) {
      const errorMessage = parseBackendError(err);
      setError(err as Error);
      console.error('❌ Erreur lors de l\'upload du CV:', err);
      
      // Afficher une notification d'erreur détaillée
      toast.showToast?.({ 
        type: 'error', 
        title: 'Échec de l\'upload du CV', 
        message: errorMessage
      });
      
      throw err;
    } finally {
      setIsUpdating(false);
    }
  };

  return {
    company,
    loading,
    isUpdating,
    error,
    usingMockData,
    // ✨ NOUVEAUX: États du cache IndexedDB
    dataSource,        // 'cache' | 'network' | 'none' - D'où viennent les données
    isRevalidating,    // true si revalidation backend en cours
    // Actions
    createCompany,
    updateCompany,
    uploadLogo,
    uploadOwnerCV,
    refreshCompany
  };
}